import {
  TELECONSULTORIA_GUEST_ALREADY_INVITED,
  TELECONSULTORIA_INVITE_PENDING,
} from "../../constants";
import {
  ensureWherebyRoom,
  loadAppointment,
} from "../../services/applyTeleconsultoriaInvite";
import type { TeleconsultoriaGuest } from "../../types";
import { teleconsultoriaGuestOfAppointment } from "../../utils/actors";
import { wherebyRoomAsArray } from "../../utils/virtualService";
import { guestPractitionerRoleReference } from "../helpers/practitionerRoleReferences";
import { buildInviteTask } from "../transformers/inviteTaskTransformers";
import { InviteActionError } from "./inviteActionError";
import { createInviteTask, findPendingInviteTasks } from "./inviteTaskService";

interface SendInviteParams {
  appointment: fhir5.Appointment;
  guest: TeleconsultoriaGuest;
  /** PractitionerRole do host (quem convida). */
  hostRoleReference: string;
  hostDisplay: string;
}

export interface SendInviteResult {
  task: fhir5.Task;
  roomUrl: string;
  /** Appointment em memória com a sala (o host entra na videochamada já). */
  appointment: fhir5.Appointment;
}

/**
 * Host convida: garante a sala e cria a Task de convite.
 * NÃO inclui o convidado no Appointment; isso só acontece quando ele aceita.
 * Regra de 1 teleconsultor: bloqueia se já há convidado CON ou convite pendente.
 */
export async function sendTeleconsultoriaInvite({
  appointment,
  guest,
  hostRoleReference,
  hostDisplay,
}: SendInviteParams): Promise<SendInviteResult> {
  const fresh = await loadAppointment(appointment);
  if (!fresh.id) throw new Error("Appointment sem id");

  if (teleconsultoriaGuestOfAppointment(fresh).practitioner) {
    throw new InviteActionError(TELECONSULTORIA_GUEST_ALREADY_INVITED);
  }
  const pending = await findPendingInviteTasks(fresh.id);
  if (pending.length) throw new InviteActionError(TELECONSULTORIA_INVITE_PENDING);

  const guestRoleReference = guestPractitionerRoleReference(guest);
  if (!guestRoleReference || !hostRoleReference) {
    throw new InviteActionError(
      "Não foi possível identificar a organização do teleconsultor. Atualize a lista e tente de novo.",
    );
  }

  const room = await ensureWherebyRoom(fresh);
  const task = await createInviteTask(
    buildInviteTask({
      appointment: fresh,
      guest,
      guestRoleReference,
      hostRoleReference,
      hostDisplay,
    }),
  );

  return {
    task,
    roomUrl: room.addressUrl,
    appointment: { ...fresh, virtualService: wherebyRoomAsArray(room) },
  };
}
